import { LanguageCode } from '@ca/common'
import { DealType } from '@ca/common/types'
import Glass from '@ui/elements/Glass'
import Checkbox from '@ui/forms/Checkbox'
import Select from '@ui/forms/Select'
import { SelectOption } from '@ui/forms/Select/SelectProps'
import Typography from '@ui/typography'
import { trpc } from 'api'
import { router } from 'expo-router'
import { DevToggle, DevToggles, useDevToggles } from 'hooks/dev/useDevToggles'
import useOfflineModal from 'hooks/useOfflineModal'
import useUpdateModal from 'hooks/useUpdateModal'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Platform, ScrollView, View } from 'react-native'
import useRemoteConfigParams, {
  useAppRegion,
} from 'storage/useRemoteConfigParams'
import cn from '@ui/utils/cn'
import { adminMenuItems } from 'utils/dev/devMenuItems'
import { DevAction } from '../utils/dev/DevActions'
import Button from '@ui/buttons/Button'

const DevToggleSelect = <T extends DevToggle>({
  name,
  label,
  options,
}: {
  name: T
  label: string
  options: SelectOption<DevToggles[NoInfer<T>]>[]
}) => {
  const value = useDevToggles((state) => state[name])
  const { toggle } = useDevToggles()
  return (
    <Select
      options={options}
      onChange={(v) => toggle(name, v)}
      label={label}
      value={value}
    />
  )
}

const PUBLIC_VERSION = -1
const PREVIEW_VERSION = -2

const ConfigPreviewToggle = () => {
  const { setState, params, setVersionId, setRegion } = useRemoteConfigParams()
  const region = useAppRegion()
  const { i18n } = useTranslation()

  useEffect(() => {
    if (!region.languages.interface.includes(i18n.language as LanguageCode)) {
      i18n.changeLanguage(region.languages.default)
    }
  }, [region, i18n])

  const val =
    params.versionId ??
    (params.state === 'public' ? PUBLIC_VERSION : PREVIEW_VERSION)

  const regions = trpc.config.regions.useQuery()

  const versions = trpc.config.versions.useQuery({
    region: params.region,
  })

  const versionOptions = useMemo(() => {
    const remote = versions.data?.map((v) => ({
      label: v.description,
      value: v.id,
    }))
    return [
      {
        label: 'Public - latest published version',
        value: PUBLIC_VERSION,
      },
      {
        label: 'Preview - current configuration',
        value: PREVIEW_VERSION,
      },

      ...(remote || []),
    ]
  }, [versions.data])

  const regionOptions = useMemo(() => {
    return (
      regions.data?.map((region) => ({
        label: region.name,
        value: region.countryCode,
      })) || []
    )
  }, [regions.data])

  return (
    <View className="gap-4">
      <Select
        options={regionOptions}
        onChange={setRegion}
        label="Region"
        value={region.code}
      />
      <Select
        options={versionOptions}
        onChange={(v) => {
          if (v !== PUBLIC_VERSION && v !== PREVIEW_VERSION) {
            setVersionId(v)
          }
          if (v === PUBLIC_VERSION) {
            setState('public')
            setVersionId(undefined)
          }
          if (v === PREVIEW_VERSION) {
            setState('preview')
            setVersionId(undefined)
          }
        }}
        label="Version"
        value={val}
      />
    </View>
  )
}

const items = adminMenuItems.map((item) => ({
  name: item.name,
  callback: () => {
    router.dismiss()
    item.callback()
  },
}))

export default function AdminPage() {
  const updateModal = useUpdateModal()
  const offlineModal = useOfflineModal()

  return (
    <Glass className={cn('flex', Platform.OS !== 'web' && 'flex-1')} blur={100}>
      <ScrollView className="flex flex-1">
        <View className="gap-16 p-12">
          <View className="gap-4 ">
            <Typography variant="H5">Settings</Typography>
            <ConfigPreviewToggle />
            <DevToggleSelect
              options={[
                {
                  label: 'Enabled',
                  value: true,
                },
                {
                  label: 'Disabled',
                  value: false,
                },
              ]}
              name="idleLogout"
              label="Idle logout"
            />
            <DevToggleSelect
              options={[
                {
                  label: 'Back',
                  value: 'back',
                },
                {
                  label: 'Front',
                  value: 'front',
                },
              ]}
              name="qrCameraType"
              label="Qr Camera"
            />
            <DevToggleSelect
              options={[
                {
                  label: 'Mock',
                  value: true,
                },
                {
                  label: 'Sdk',
                  value: false,
                },
              ]}
              name="measurementsMock"
              label="Measure"
            />
            <DevToggleSelect
              options={[
                {
                  label: 'brillen',
                  value: 'normal',
                },
                {
                  label: 'TGBO',
                  value: 'tgbo',
                },
                {
                  label: 'All',
                  value: Object.values(DealType).join(','),
                },
                {
                  label: 'default',
                  value: undefined,
                },
              ]}
              name="dealTypes"
              label="Deal types"
            />
            <DevToggleSelect
              options={[
                {
                  label: 'off',
                  value: false,
                },
                {
                  label: 'on',
                  value: true,
                },
                {
                  label: 'default',
                  value: undefined,
                },
              ]}
              name="avatar"
              label="Avatar"
            />
          </View>
          <View className="gap-4">
            <Typography variant={'MDB'}>Show Screen</Typography>
            <View className="flex flex-row gap-4">
              <Button
                label="Update"
                size="sm"
                variant="outlined"
                onPress={() => updateModal.setForced(!updateModal.isForced)}
              />
              <Button
                label="Offline"
                size="sm"
                variant="outlined"
                onPress={() =>
                  offlineModal.isOpen
                    ? offlineModal.close()
                    : offlineModal.open()
                }
              />
              <Button
                label="Not Found"
                size="sm"
                variant="outlined"
                onPress={() => router.replace('/not-found')}
              />
            </View>
          </View>
          <View className="gap-4">
            <Typography variant="H5">Actions</Typography>
            {items.map((item, index) => {
              return <DevAction key={`${item.name}${index}`} item={item} />
            })}
            <DevAction
              icon="x"
              item={{
                name: 'Close',
                callback: () => {
                  router.dismiss()
                },
              }}
            />
          </View>
        </View>
      </ScrollView>
    </Glass>
  )
}
